/**
 * Shared health status utilities for entities (bikes, stations, etc.)
 * Consolidates common health calculation logic to avoid duplication.
 */

// Re-export formatDuration from dateUtils for convenience
export { formatDuration } from "./dateUtils";

export type HealthStatus = "good" | "warning" | "critical" | "unknown";
export type LastSeenStatus = HealthStatus | "never";

export interface HealthThresholds {
  /** Hours until warning status (default: 48) */
  warningHours?: number;
  /** Hours until critical status (default: 168 = 7 days) */
  criticalHours?: number;
}

export interface BatteryThresholds {
  /** Percentage at or below which status is critical (default: 10) */
  criticalPercent?: number;
  /** Percentage at or below which status is warning (default: 20) */
  warningPercent?: number;
}

const DEFAULT_LAST_SEEN_THRESHOLDS: Required<HealthThresholds> = {
  warningHours: 48,
  criticalHours: 168,
};

const DEFAULT_BATTERY_THRESHOLDS: Required<BatteryThresholds> = {
  criticalPercent: 10,
  warningPercent: 20,
};

/**
 * Calculates hours since a given date string
 * @returns hours since the date, or null if invalid/missing
 */
export function getHoursSince(date: string | null | undefined): number | null {
  if (!date) return null;
  try {
    const dateToFormat = date.endsWith("Z") ? date : `${date}Z`;
    const hoursSince = new Date().getTime() - new Date(dateToFormat).getTime();
    return Math.floor(hoursSince / (1000 * 60 * 60));
  } catch {
    return null;
  }
}

/**
 * Determines health status based on last seen date
 */
export function getLastSeenStatus(
  date: string | null | undefined,
  thresholds?: HealthThresholds
): LastSeenStatus {
  if (!date) return "never";

  const hours = getHoursSince(date);
  if (hours === null) return "never";

  const { warningHours, criticalHours } = {
    ...DEFAULT_LAST_SEEN_THRESHOLDS,
    ...thresholds,
  };

  if (hours > criticalHours) return "critical";
  if (hours > warningHours) return "warning";
  return "good";
}

/**
 * Determines health status based on battery percentage
 */
export function getBatteryStatus(
  level: number | null | undefined,
  thresholds?: BatteryThresholds
): HealthStatus {
  if (level === null || level === undefined) return "unknown";

  const { criticalPercent, warningPercent } = {
    ...DEFAULT_BATTERY_THRESHOLDS,
    ...thresholds,
  };

  if (level <= criticalPercent) return "critical";
  if (level <= warningPercent) return "warning";
  return "good";
}

/**
 * Determines health status based on fault count
 */
export function getFaultStatus(count: number): HealthStatus {
  if (count === 0) return "good";
  if (count <= 2) return "warning";
  return "critical";
}

/**
 * Returns Tailwind color class for battery level
 */
export function getBatteryColorClass(level: number | null | undefined): string {
  if (level === null || level === undefined) return "tw-text-muted-foreground";
  if (level <= 10) return "tw-text-red-500";
  if (level <= 20) return "tw-text-amber-500";
  if (level <= 50) return "tw-text-yellow-500";
  return "tw-text-green-500";
}

/**
 * Returns Tailwind text color class for health status
 */
export function getHealthStatusTextColor(
  status: HealthStatus | LastSeenStatus
): string {
  switch (status) {
    case "good":
      return "tw-text-green-600";
    case "warning":
      return "tw-text-yellow-600";
    case "critical":
    case "never":
      return "tw-text-red-600";
    default:
      return "tw-text-muted-foreground";
  }
}

/**
 * Returns Tailwind background color class for health status bars
 */
export function getHealthStatusBarColor(
  status: HealthStatus | LastSeenStatus
): string {
  switch (status) {
    case "good":
      return "tw-bg-green-500";
    case "warning":
      return "tw-bg-yellow-500";
    case "critical":
    case "never":
      return "tw-bg-red-500";
    default:
      return "tw-bg-muted-foreground/50";
  }
}

/**
 * Returns Tailwind background color class with opacity for status badges
 */
export function getHealthStatusBgColor(
  status: HealthStatus | LastSeenStatus
): string {
  switch (status) {
    case "good":
      return "tw-bg-green-500/10";
    case "warning":
      return "tw-bg-yellow-500/10";
    case "critical":
    case "never":
      return "tw-bg-red-500/10";
    default:
      return "tw-bg-muted";
  }
}

// ============================================================================
// Field Warning Utilities
// These create warning objects compatible with the FieldWarning component
// ============================================================================

export type WarningSeverity = "low" | "medium" | "high";
export type WarningType = "stale" | "battery" | "location" | "generic";

export interface FieldWarningData {
  message: string;
  severity: WarningSeverity;
  type: WarningType;
}

/**
 * Maps HealthStatus to WarningSeverity for FieldWarning component
 */
function statusToSeverity(
  status: HealthStatus | LastSeenStatus
): WarningSeverity | null {
  switch (status) {
    case "critical":
    case "never":
      return "high";
    case "warning":
      return "medium";
    default:
      return null;
  }
}

/**
 * Creates a battery warning object for FieldWarning component
 * Returns undefined if battery level is healthy or unknown
 */
export function getBatteryWarning(
  level: number | null | undefined,
  thresholds?: BatteryThresholds
): FieldWarningData | undefined {
  if (level === null || level === undefined) return undefined;

  const status = getBatteryStatus(level, thresholds);
  const severity = statusToSeverity(status);
  if (!severity) return undefined;

  const messagePrefix =
    status === "critical" ? "Critical battery" : "Low battery";
  return {
    message: `${messagePrefix}: ${level}%`,
    severity,
    type: "battery",
  };
}

export interface LastSeenWarningOptions {
  /** Function to format hours into a human-readable duration */
  formatDuration: (hours: number) => string;
  /** Message to show when date is null/undefined */
  neverSeenMessage: string;
  /** Message template with {duration} placeholder */
  lastSeenAgoMessage: (duration: string) => string;
  /** Hours threshold for low severity warning (default: 24) */
  lowHours?: number;
  /** Health thresholds for warning/critical status */
  thresholds?: HealthThresholds;
}

/**
 * Creates a last-seen warning object for FieldWarning component
 * Returns undefined if recently seen (within lowHours threshold)
 */
export function getLastSeenWarning(
  date: string | null | undefined,
  options: LastSeenWarningOptions
): FieldWarningData | undefined {
  const {
    formatDuration,
    neverSeenMessage,
    lastSeenAgoMessage,
    lowHours = 24,
    thresholds,
  } = options;

  if (!date) {
    return { message: neverSeenMessage, severity: "high", type: "stale" };
  }

  const hours = getHoursSince(date);
  if (hours === null) return undefined;

  const status = getLastSeenStatus(date, thresholds);

  // Check for low severity (between lowHours and warning threshold)
  const warningHours =
    thresholds?.warningHours ?? DEFAULT_LAST_SEEN_THRESHOLDS.warningHours;
  if (hours > lowHours && hours <= warningHours) {
    return {
      message: lastSeenAgoMessage(formatDuration(hours)),
      severity: "low",
      type: "stale",
    };
  }

  const severity = statusToSeverity(status);
  if (!severity) return undefined;

  return {
    message: lastSeenAgoMessage(formatDuration(hours)),
    severity,
    type: "stale",
  };
}
